import {
  APIModelAttributes,
  APIOrgTripleAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  AssetFolder,
  NamedEntity,
  TripleDirection,
} from '@ardoq/api-types';
import { toModelTypes } from '@ardoq/scope-data';
import { ModelTypes } from '@ardoq/data-model';
import { getAssetPathName } from 'utils/hierarchy';
import { canDo, PermissionContext } from '@ardoq/access-control';
import { toId } from '@ardoq/core';
import { StreamCollection } from 'streams/utils/streamUtils';
import intersection from 'lodash/intersection';

export type TripleWithContext = {
  isOrgTriple: boolean;
  sourceType: string;
  referenceType: string;
  targetType: string;
  direction: TripleDirection;
  useCases: APIOrgTripleAttributes['useCases'];
  sourceInstances: {
    componentTypeId: string;
    referenceTypeId: number;
    workspaceId: string;
    workspaceName: string;
    path: string[];
  }[];
  sourceInstancesWithWritePermissions: {
    componentTypeId: string;
    referenceTypeId: number;
    workspaceId: string;
    workspaceName: string;
    path: string[];
  }[];
  targetInstances: {
    componentTypeId: string;
    workspaceId: string;
    workspaceName: string;
    path: string[];
  }[];
  targetInstancesWithWritePermissions: {
    componentTypeId: string;
    workspaceId: string;
    workspaceName: string;
    path: string[];
  }[];
  tripleKey: string;
};

export type TripleBase = {
  sourceType: string;
  referenceType: string;
  targetType: string;
  direction?: TripleDirection;
  useCases?: NamedEntity[];
  ardoq?: {
    ['entity-type']: string;
  };
  tripleKey: string;
};

export type ToTriplesWithContext = {
  triples: TripleBase[];
  workspaces: StreamCollection<APIWorkspaceAttributes>;
  models: StreamCollection<APIModelAttributes>;
  assetFolders: AssetFolder[];
  permissionContext: PermissionContext;
};

export const toTriplesWithContext = ({
  triples,
  workspaces,
  models,
  assetFolders,
  permissionContext,
}: ToTriplesWithContext): TripleWithContext[] => {
  const typesByModelId = toModelTypes(models.models);
  const modelToWorkspace = Object.fromEntries(
    workspaces.models.map(ws => [ws.componentModel, ws])
  );
  const { referenceTypeInstances, componentTypeInstances } =
    getTypeNameToTypeRecords(models, modelToWorkspace, typesByModelId);

  const folderPaths = workspaces.models.map(ws => {
    const path = getAssetPathName(assetFolders, ws._id);
    return [ws._id, path];
  });
  const workspaceIdToFolderPath: Record<string, string[]> =
    Object.fromEntries(folderPaths);
  const workspaceIdsWithEditPermissions = new Set(
    workspaces.models
      .filter(ws =>
        canDo({
          permissionContext,
          action: 'workspace/edit',
          resourceId: ws._id,
        })
      )
      .map(toId)
  );

  return triples.map(triple =>
    toOrgTripleWithContext(
      triple,
      referenceTypeInstances,
      componentTypeInstances,
      workspaces,
      workspaceIdToFolderPath,
      workspaceIdsWithEditPermissions
    )
  );
};

type ComponentTypeInstances = Record<
  string,
  {
    typeId: string;
    workspaceId: string;
  }[]
>;

type ReferenceTypeInstances = Record<
  string,
  {
    typeId: number;
    workspaceId: string;
  }[]
>;

const getTypeNameToTypeRecords = (
  models: StreamCollection<APIModelAttributes>,
  modelToWorkspace: Record<ArdoqId, APIWorkspaceAttributes>,
  typesByModelId: Record<string, ModelTypes>
): {
  referenceTypeInstances: ReferenceTypeInstances;
  componentTypeInstances: ComponentTypeInstances;
} => {
  const referenceTypeInstances: ReferenceTypeInstances = {};
  const componentTypeInstances: ComponentTypeInstances = {};

  Object.entries(typesByModelId)
    .filter(([modelId]) => !models.byId[modelId]?.useAsTemplate)
    .forEach(([modelId, modelTypes]) => {
      Object.values(modelTypes.referenceTypesById).forEach(refType => {
        const { name, id } = refType;
        if (!referenceTypeInstances[name]) {
          referenceTypeInstances[name] = [];
        }
        referenceTypeInstances[name].push({
          typeId: id,
          workspaceId: modelToWorkspace[modelId]?._id || '',
        });
      });
      Object.values(modelTypes.componentTypesById).forEach(compType => {
        const { name, id } = compType;
        if (!componentTypeInstances[name]) {
          componentTypeInstances[name] = [];
        }
        componentTypeInstances[name].push({
          typeId: id,
          workspaceId: modelToWorkspace[modelId]?._id || '',
        });
      });
    });

  return { referenceTypeInstances, componentTypeInstances };
};

const toOrgTripleWithContext = (
  triple: TripleBase,
  referenceTypeInstances: ReferenceTypeInstances,
  componentTypeInstances: ComponentTypeInstances,
  workspaces: StreamCollection<APIWorkspaceAttributes>,
  workspaceIdToFolderPath: Record<string, string[]>,
  workspaceIdsWithEditPermissions: Set<string>
): TripleWithContext => {
  const {
    sourceType,
    referenceType,
    targetType,
    useCases,
    ardoq,
    direction,
    tripleKey,
  } = triple;
  const componentTypeWorkspaceIds = getWorkspaceIds(
    componentTypeInstances[sourceType] ?? []
  );
  const referenceTypeWorkspaceIds = getWorkspaceIds(
    referenceTypeInstances[referenceType] ?? []
  );
  const sourceWorkspaceIds = intersection(
    componentTypeWorkspaceIds,
    referenceTypeWorkspaceIds
  );
  const sourceInstances = sourceWorkspaceIds
    .filter(wsId => workspaces.byId[wsId])
    .map(wsId => {
      const componentTypeId = getTypeIdWithWorkspaceId(
        componentTypeInstances[sourceType] ?? [],
        wsId
      );
      const referenceTypeId = getTypeIdWithWorkspaceId(
        referenceTypeInstances[referenceType] ?? [],
        wsId
      );
      return {
        componentTypeId,
        referenceTypeId,
        workspaceId: wsId,
        workspaceName: workspaces.byId[wsId]!.name,
        path: workspaceIdToFolderPath[wsId],
      };
    });

  const sourceInstancesWithWritePermissions = sourceInstances.filter(instance =>
    workspaceIdsWithEditPermissions.has(instance.workspaceId)
  );

  const targetInstances = (componentTypeInstances[targetType] ?? [])
    .filter(({ workspaceId }) => workspaces.byId[workspaceId])
    .map(({ typeId: componentTypeId, workspaceId }) => ({
      componentTypeId,
      workspaceId,
      workspaceName: workspaces.byId[workspaceId]!.name,
      path: workspaceIdToFolderPath[workspaceId],
    }));
  const targetInstancesWithWritePermissions = targetInstances.filter(instance =>
    workspaceIdsWithEditPermissions.has(instance.workspaceId)
  );
  const isOrgTriple = Boolean(ardoq && ardoq['entity-type'] === 'org-triple');

  return {
    isOrgTriple,
    sourceType,
    referenceType,
    targetType,
    // TODO not sure about this one
    direction: direction || 'outgoing',
    useCases: useCases || [],
    sourceInstances,
    sourceInstancesWithWritePermissions,
    targetInstances,
    targetInstancesWithWritePermissions,
    tripleKey,
  };
};

const getWorkspaceIds = <T extends { workspaceId: string }>(instances: T[]) =>
  (instances ?? []).map(({ workspaceId }) => workspaceId);

const getTypeIdWithWorkspaceId = <T = number | string>(
  instances: { workspaceId: string; typeId: T }[],
  workspaceId: string
) => instances.find(instance => instance.workspaceId === workspaceId)!.typeId;
